import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Empty } from 'antd'
import classNames from 'classnames'
import dayjs from '@store/timetable/dayjs.config'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import { IcoRemove } from '@/utils/icons'
import { useAddAlert } from '@/hooks/useAddAlert'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import modalStyles from '@/components/modals/index.module.css'
import timetableStyles from '@/components/modals/Timetable/EditLessonOrMeeting/common.module.css'
import { IGroupLessonPaymentByLessonItem } from '../../../../utils/types'
import {
  getStatusColor,
  getStatusDescription,
  canSubmitLessonStatuses,
  REPORTABLE_LESSON_STATUS_OPTIONS,
} from '../../../../utils/lessonStatus'
import {
  getStudentsLessonStatuses,
  IStudentLessonStatus,
  updateStudentsLessonStatuses,
} from './utils/api'
import cn from './index.module.css'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import UserCard from '@pages/Admin/Manage/tabs/ClassesPage/components/ShowUsersModal/components/UserCard/UserCard'
import Preloader from '@UI/Preloader'
import Select from '@UI/New/Select'
import TextAreaField from '@pages/Admin/CourseSettings/components/TextAreaField/TextAreaField'

interface IChangeStudentsLessonStatusModal {
  open: boolean
  lesson: IGroupLessonPaymentByLessonItem | null
  onDismiss: () => void
  editLessonStatus?: boolean
  onSuccess?: () => void
}

const formatLessonDate = (date?: string | null) => {
  if (!date) return '-'

  const value = dayjs(date)

  return value.isValid() ? value.format('DD.MM.YYYY HH:mm') : '-'
}

const ChangeStudentsLessonStatusModal: FC<IChangeStudentsLessonStatusModal> = (
  props,
) => {
  const [submitting, setSubmitting] = useState(false)
  const dismiss = () => { if (!submitting) props.onDismiss() }
  return (
    <Modal
      active={props.open}
      className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
      setActive={dismiss}
    >
      <Body {...props} submitting={submitting} setSubmitting={setSubmitting} />
    </Modal>
  )
}

const Body: FC<IChangeStudentsLessonStatusModal & { submitting: boolean; setSubmitting: (value: boolean) => void }> = ({
  lesson,
  open,
  onDismiss,
  onSuccess,
  editLessonStatus = false,
  submitting,
  setSubmitting,
}) => {
  const addAlert = useAddAlert()
  const [loading, setLoading] = useState(false)
  const saving = useRef(false)
  const [step, setStep] = useState<'status' | 'attendance'>('status')
  const [lessonStatus, setLessonStatus] = useState('5')
  const [lessonComment, setLessonComment] = useState('')
  const initialLessonComment = useRef('')
  const [rows, setRows] = useState<IStudentLessonStatus[]>([])
  const [initialRows, setInitialRows] = useState<IStudentLessonStatus[]>([])

  useEffect(() => {
    if (!open || !lesson?.id) return
    let cancelled = false
    setStep(editLessonStatus ? 'status' : 'attendance')
    setLessonStatus(['5', '10', '11', '12'].includes(String(lesson.status)) ? String(lesson.status) : '5')
    // comment в строке отчёта — сводка «Ученик: статус», а не комментарий занятия.
    const ownComment = lesson.lesson_comment !== undefined ? lesson.lesson_comment ?? '' : lesson.comment ?? ''
    initialLessonComment.current = ownComment
    setLessonComment(ownComment)
    setRows([])
    setInitialRows([])
    setLoading(true)
    getStudentsLessonStatuses(Number(lesson.id)).then((response) => {
      if (cancelled) return
      setLoading(false)
      if (!response.success) {
        addAlert({ title: 'Ошибка', text: response.error || 'Не удалось загрузить учеников занятия', type: 'error' })
        return
      }
      setRows(response.data ?? [])
      setInitialRows(response.data ?? [])
    })
    return () => { cancelled = true }
  }, [lesson?.id, open, editLessonStatus])

  // В отменённом занятии «Проведено» у ученика недопустимо: бэкенд отклонит такой отчёт.
  const effectiveLessonStatus = editLessonStatus ? lessonStatus : String(lesson?.status ?? '')
  const studentStatusOptions = ['10', '11', '12'].includes(effectiveLessonStatus)
    ? REPORTABLE_LESSON_STATUS_OPTIONS.filter((item) => item.value !== '5')
    : REPORTABLE_LESSON_STATUS_OPTIONS

  const initialRowsByStudent = useMemo(() => {
    return new Map(initialRows.map((row) => [row.student.id, row]))
  }, [initialRows])

  const changedRows = useMemo(() => {
    return rows.filter((row) => {
      const initialRow = initialRowsByStudent.get(row.student.id)

      if (!initialRow) return true

      return (
        String(initialRow.status) !== String(row.status) ||
        String(initialRow.comment ?? '') !== String(row.comment ?? '')
      )
    })
  }, [initialRowsByStudent, rows])

  const updateRow = (
    studentId: number,
    field: 'status' | 'comment',
    value: string,
  ) => {
    setRows((prev) =>
      prev.map((row) =>
        row.student.id === studentId ? { ...row, [field]: value } : row,
      ),
    )
  }

  const onSave = async () => {
    if (saving.current || loading) return
    if (editLessonStatus && step === 'status') {
      if (lessonStatus !== '5') {
        // Отмена: всем ученикам статус занятия, дальше его можно поменять на шаге посещаемости.
        setRows((prev) => prev.map((row) => row.joined_after_lesson ? row : { ...row, status: lessonStatus }))
      } else if (['10', '11', '12'].includes(String(lesson?.status))) {
        // При возврате из отмены её общие статусы не являются посещаемостью.
        setRows((prev) => prev.map((row) => row.joined_after_lesson ? row : { ...row, status: '5' }))
      } else {
        setRows((prev) => prev.map((row) => row.joined_after_lesson || row.status ? row : { ...row, status: '5' }))
      }
      setStep('attendance')
      return
    }
    if (!lesson?.id) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось определить занятие',
        type: 'error',
      })
      return
    }

    if (!editLessonStatus && !changedRows.length) {
      addAlert({ title: 'Нет изменений для сохранения' })
      return
    }
    const submittedRows = editLessonStatus ? rows : changedRows
    if (!canSubmitLessonStatuses(editLessonStatus ? submittedRows : rows)) {
      addAlert({ title: 'Укажите статус каждого ученика занятия', type: 'error' })
      return
    }

    saving.current = true
    setSubmitting(true)

    const response = await updateStudentsLessonStatuses(
      Number(lesson.id),
      submittedRows.map((row) => ({
        id: row.id,
        student: row.student,
        // Без статуса уходит пустая строка, а не «null»: бэкенд читал её как «Н» и снимал списание.
        status: row.status ?? '',
        comment: row.comment ?? '',
      })),
      editLessonStatus ? lessonStatus : undefined,
      // Неизменённый комментарий не отправляем, чтобы не перезаписать его устаревшим текстом.
      editLessonStatus && lessonComment !== initialLessonComment.current ? lessonComment : undefined,
    )

    saving.current = false
    setSubmitting(false)

    if (!response.success) {
      addAlert({
        title: 'Ошибка',
        text: response.error || 'Не удалось сохранить статусы учеников',
        type: 'error',
      })
      return
    }

    addAlert({ title: 'Статусы учеников сохранены' })
    onDismiss()
    onSuccess?.()
  }

  return (
    <div className={mclasses.modalContainer}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={modalStyles.modalHeaderBigTitle}>
            {editLessonStatus && step === 'status' ? 'Изменить статус занятия' : 'Посещаемость занятия'}
          </span>
        </div>
        <div className={mclasses.closeIcon} onClick={() => { if (!saving.current) onDismiss() }}>
          <IcoRemove />
        </div>
      </div>
      <div className={classNames(mclasses.formWrapper, cn.form, editLessonStatus && step === 'status' && cn.statusForm)}>
        {editLessonStatus && step === 'status' ? (
          <div className={timetableStyles.lessonMetaText}>
            <span>Группа(ы): {lesson?.group?.name ?? '-'}</span>
            <span>Дата и время: {formatLessonDate(lesson?.date_time)}</span>
          </div>
        ) : (
          <div className={cn.lessonInfo}>
            <FieldWrapper label={'Курс'} className={cn.lessonInfoItem}>
              <span className={cn.lessonInfoValue}>
                {lesson?.course?.name ?? '-'}
              </span>
            </FieldWrapper>
            <FieldWrapper label={'Группа'} className={cn.lessonInfoItem}>
              <span className={cn.lessonInfoValue}>
                {lesson?.group?.name ?? '-'}
              </span>
            </FieldWrapper>
            <FieldWrapper label={'Дата и время'} className={cn.lessonInfoItem}>
              <span className={cn.lessonInfoValue}>
                {formatLessonDate(lesson?.date_time)}
              </span>
            </FieldWrapper>
          </div>
        )}
        {editLessonStatus && step === 'status' ? (
          <>
            <FieldWrapper label="Статус занятия">
              <Select hideSearchIcon fields={{ style: { width: '100%' }, value: lessonStatus, showSearch: false, options: REPORTABLE_LESSON_STATUS_OPTIONS.filter((item) => item.value !== '9'), onChange: (value: string) => setLessonStatus(value) }} />
            </FieldWrapper>
            <TextAreaField
              label="Напишите комментарий"
              placeholder="комментарий"
              value={lessonComment}
              autoSize={{ minRows: 3, maxRows: 6 }}
              onChange={(event) => setLessonComment(event.target.value)}
            />
          </>
        ) : loading ? (
          <div className={cn.loading}>
            <Preloader />
          </div>
        ) : rows.length ? (
          <div className={cn.table}>
            <div className={classNames(cn.row, cn.headerRow)}>
              <span>Ученик</span>
              <span>Статус</span>
              <span>Комментарий</span>
            </div>
            <div className={cn.rows}>
              {rows.map((row) => (
                <div className={cn.row} key={row.id}>
                  <div className={cn.studentCell}>
                    <UserCard user={row.student} />
                    {row.joined_after_lesson && (
                      <span className={cn.joinedAfterLesson}>
                        Добавлен в группу после занятия
                      </span>
                    )}
                  </div>
                  <div className={cn.statusCell}>
                    <span
                      className={cn.statusMarker}
                      style={{ backgroundColor: getStatusColor(row.status ?? '') }}
                      title={getStatusDescription(row.status ?? '')}
                    />
                    <Select
                      wrapperClassName={cn.statusSelect}
                      compact
                      hideSearchIcon
                      fields={{
                        style: { width: '100%' },
                        value: row.status,
                        placeholder: 'Не отмечен',
                        options: studentStatusOptions,
                        showSearch: false,
                        onChange: (value: string) =>
                          updateRow(
                            row.student.id,
                            'status',
                            String(value ?? '0'),
                          ),
                      }}
                    />
                  </div>
                  <TextAreaField
                    className={cn.comment}
                    value={row.comment}
                    placeholder="Комментарий"
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    onChange={(event) =>
                      updateRow(row.student.id, 'comment', event.target.value)
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Ученики не найдены"
          />
        )}
      </div>
      <div className={classNames(mclasses.actions, editLessonStatus && step === 'status' && cn.statusActions)}>
        <Button color="gray" onClick={onDismiss} disabled={submitting}>
          Отменить
        </Button>
        <Button
          color="primary"
          onClick={onSave}
          isLoading={submitting}
          disabled={loading || submitting || rows.length === 0 || (!editLessonStatus && (changedRows.length === 0 || !canSubmitLessonStatuses(rows)))}
        >
          {editLessonStatus && step === 'status' ? 'Далее' : 'Сохранить'}
        </Button>
      </div>
    </div>
  )
}

export default ChangeStudentsLessonStatusModal
